/* eslint-disable no-magic-numbers */
import {
  HealthScore,
  RevenueChart,
  CostStructure,
  LiquidityPanel,
  Insight,
  ExecutiveSummary
} from '@futrli-sdk/predict_analysis';
import { IntlShape } from 'react-intl';

import { getCurrencySymbolFromCode } from '@futrli/localisation';

import {
  formatMetricValue,
  getKpiLabel,
  mapSentimentToVariant,
  mapInsightStatusToAccordionVariant,
  mapImpactToLabel,
  mapImpactToVariant,
  COST_SEGMENT_COLORS
} from './format-utils';
import {
  MappedHealthScore,
  MappedRevenueChart,
  MappedCostStructure,
  MappedLiquidity,
  MappedInsight,
  MappedExecutiveSummary,
  MappedGrowthMetric,
  MappedRecommendation
} from './types';

export const mapHealthScore = (hs: HealthScore, intl: IntlShape): MappedHealthScore => {
  const statusIndicators: MappedHealthScore['statusIndicators'] = [];
  if (Number.isFinite(hs.green_count)) {
    statusIndicators.push({
      label: intl.formatMessage(
        { id: 'aiInsights.health.insightsHealthy', defaultMessage: '{count} healthy' },
        { count: hs.green_count }
      ),
      colorVariant: 'positive'
    });
  }
  if (Number.isFinite(hs.yellow_count)) {
    statusIndicators.push({
      label: intl.formatMessage(
        { id: 'aiInsights.health.insightsToWatch', defaultMessage: '{count} to watch' },
        { count: hs.yellow_count }
      ),
      colorVariant: 'warning'
    });
  }
  if (Number.isFinite(hs.red_count)) {
    statusIndicators.push({
      label: intl.formatMessage(
        { id: 'aiInsights.health.insightsAtRisk', defaultMessage: '{count} at risk' },
        { count: hs.red_count }
      ),
      colorVariant: 'negative'
    });
  }
  return {
    score: hs.overall_score,
    overallStatus: hs.overall_status,
    title: hs.headline.replace(/^Business health:\s*/i, ''),
    description: hs.description,
    statusIndicators,
    scoreDeltas: (hs.score_components ?? []).map(component => component.delta)
  };
};

export const mapRevenueChart = (
  chart: RevenueChart,
  currencyCode: string,
  title: string,
  subtitle?: string
): MappedRevenueChart => {
  const sorted = [...(chart.data ?? [])].sort((a, b) => a.period.localeCompare(b.period));
  const actuals = sorted.filter(d => !d.is_forecast);
  const forecasts = sorted.filter(d => d.is_forecast);
  const symbol = getCurrencySymbolFromCode(currencyCode);

  return {
    title,
    subtitle,
    months: sorted.map(d => d.period),
    actuals: actuals.map(d => d.value),
    forecast: forecasts.map(d => d.value),
    yAxisPrefix: symbol
  };
};

export const mapCostStructure = (
  cs: CostStructure,
  currencyCode: string,
  title: string,
  totalLabel: string,
  subtitle?: string
): MappedCostStructure => {
  const symbol = getCurrencySymbolFromCode(currencyCode);
  return {
    title,
    subtitle,
    totalLabel,
    segments: cs.segments.map((seg, i) => ({
      label: seg.name,
      value: seg.value,
      color: COST_SEGMENT_COLORS[i % COST_SEGMENT_COLORS.length]
    })),
    formatValue: (value: number) => `${symbol}${Math.round(value / 1000).toLocaleString()}k`,
    formatTotal: (value: number) => `${symbol}${value.toLocaleString()}`
  };
};

export const mapLiquidityPanel = (
  panel: LiquidityPanel,
  currencyCode: string,
  title: string,
  variant: 'accountant' | 'business_owner',
  intl: IntlShape,
  subtitle?: string
): MappedLiquidity => ({
  title,
  subtitle,
  metrics: (panel.metrics ?? []).map(m => {
    const kpiLabel = getKpiLabel(m.kpi_key, variant, intl);
    const periodSuffix = m.period ? ` (${m.period})` : '';
    const compact = variant === 'business_owner';
    return {
      label: `${kpiLabel}${periodSuffix}`,
      value: formatMetricValue(m.value, m.format, currencyCode, compact),
      variant: mapSentimentToVariant(m.sentiment)
    };
  })
});

export const mapInsightMetrics = (metrics: Insight['metrics'], currencyCode: string): MappedGrowthMetric[] =>
  [...metrics]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(m => ({
      label: m.label,
      value: formatMetricValue(m.value, m.format, currencyCode),
      status: mapSentimentToVariant(m.sentiment) as MappedGrowthMetric['status']
    }));

export const mapInsightRecommendations = (recs: Insight['recommendations']): MappedRecommendation[] =>
  [...recs].map(r => ({
    classification: r.classification,
    text: r.action
  }));

export const mapInsight = (insight: Insight, currencyCode: string, intl: IntlShape): MappedInsight => ({
  variant: mapInsightStatusToAccordionVariant(insight.status),
  category: insight.category,
  title: `${insight.summary.split('.')[0]}.`,
  description: insight.summary,
  impact: {
    variant: mapImpactToVariant(insight.business_impact),
    label: mapImpactToLabel(insight.business_impact, intl)
  },
  metrics: mapInsightMetrics(insight.metrics, currencyCode),
  recommendations: mapInsightRecommendations(insight.recommendations)
});

export const mapInsights = (
  insights: Insight[],
  scoreComponents: HealthScore['score_components'] | undefined,
  currencyCode: string,
  intl: IntlShape
): MappedInsight[] => {
  const hasNonZeroScoreDelta = (scoreComponents ?? []).some(component => component.delta !== 0);
  const scoreDeltaByInsightId = new Map(
    hasNonZeroScoreDelta ? (scoreComponents ?? []).map(component => [component.insight_id, component.delta]) : []
  );

  return [...insights]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(insight => ({
      ...mapInsight(insight, currencyCode, intl),
      scoreDelta: scoreDeltaByInsightId.get(insight.id)
    }));
};

export const mapExecutiveSummary = (es: ExecutiveSummary, title: string, intl: IntlShape): MappedExecutiveSummary => ({
  title,
  paragraphs: es.content
    .split('\n\n')
    .filter(p => p.trim().length > 0)
    .map(text => ({ text: text.trim() })),
  bottomLine: {
    label: intl.formatMessage({ id: 'aiInsights.executiveSummary.bottomLine', defaultMessage: 'Bottom line:' }),
    text: es.highlight
  }
});
